import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Tag, Space, Typography, message } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useIntl } from "react-intl";
import {
    useGetApiKeysQuery,
    useRevokeApiKeyMutation,
} from "../../services/nestjs-base-api";
import { IApiKey } from "../../services/nestjs-base-api/types";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ApiKeyCreate from "./ApiKeyCreate";
import ApiKeyEdit from "./ApiKeyEdit";
import { ResponsiveTable } from '../../admin/components/ResponsiveTable';

const { Title, Text } = Typography;
const { confirm } = Modal;

interface IProps {}

const ApiKeys: React.FunctionComponent<IProps> = () => {
    const { user, logout } = useAuth();
    const intl = useIntl();
    const [selectedApiKey, setSelectedApiKey] = useState<IApiKey | undefined>(undefined);
    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

    const {
        data: apiKeysResponse,
        refetch,
        error,
    } = useGetApiKeysQuery(
        { jwtToken: user.jwtToken, organisationId: user.organisation.id },
        { pollingInterval: 600000 }
    );

    const [revokeApiKey] = useRevokeApiKeyMutation();

    useEffect(() => {
        if (error) {
            if ('status' in error) {
                if (error.status === 401) {
                    logout();
                }
            }
        }
    }, [error, logout]);

    const apiKeys = useMemo(() => {
        return apiKeysResponse?.data || [];
    }, [apiKeysResponse]);

    const onCreateClose = useCallback(() => {
        setCreateModalOpen(false);
        refetch();
    }, [refetch]);

    const onEditClose = useCallback(() => {
        setEditModalOpen(false);
        setSelectedApiKey(undefined);
        refetch();
    }, [refetch]);

    const onCreateClicked = useCallback(() => {
        setCreateModalOpen(true);
    }, []);

    const onEditClicked = useCallback((apiKey: IApiKey) => {
        setSelectedApiKey(apiKey);
        setEditModalOpen(true);
    }, []);

    const onRevokeClicked = useCallback(async (apiKey: IApiKey) => {
        confirm({
            title: intl.formatMessage({ id: 'apiKeys.revokeConfirm.title' }),
            icon: <ExclamationCircleOutlined />,
            content: intl.formatMessage({ id: 'apiKeys.revokeConfirm.message' }),
            okText: intl.formatMessage({ id: 'apiKeys.actions.revoke' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'common.cancelButton' }),
            onOk: async () => {
                try {
                    await revokeApiKey({
                        jwtToken: user.jwtToken,
                        organisationId: user.organisation.id,
                        keyId: apiKey.id,
                    }).unwrap();
                    message.success('API key revoked successfully');
                    refetch();
                } catch (error) {
                    message.error('Failed to revoke API key');
                }
            },
        });
    }, [user, revokeApiKey, refetch, intl]);

    const formatScope = useCallback((scope: string) => {
        return intl.formatMessage({ id: `apiKeys.scope.${scope}` });
    }, [intl]);

    const formatStatus = useCallback((status: string) => {
        const colors = {
            active: 'green',
            revoked: 'red',
            expired: 'orange',
        };
        return (
            <Tag color={colors[status as keyof typeof colors]}>
                {intl.formatMessage({ id: `apiKeys.status.${status}` })}
            </Tag>
        );
    }, [intl]);

    const formatDate = useCallback((dateString: string | null) => {
        if (!dateString) return '-';
        return dayjs(dateString).format('MMM DD, YYYY HH:mm');
    }, []);

    const columns = [
        {
            title: intl.formatMessage({ id: 'apiKeys.table.name' }),
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: IApiKey) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{name}</div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.keyPrefix}
                    </Text>
                </div>
            ),
        },
        {
            title: intl.formatMessage({ id: 'apiKeys.table.scopes' }),
            dataIndex: 'scopes',
            key: 'scopes',
            render: (scopes: string[]) => (
                <Space size="small" wrap>
                    {scopes.map((scope) => (
                        <Tag key={scope} color="blue">
                            {formatScope(scope)}
                        </Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: intl.formatMessage({ id: 'apiKeys.table.status' }),
            dataIndex: 'status',
            key: 'status',
            render: formatStatus,
        },
        {
            title: intl.formatMessage({ id: 'apiKeys.table.lastUsed' }),
            dataIndex: 'lastUsedAt',
            key: 'lastUsedAt',
            render: formatDate,
        },
        {
            title: intl.formatMessage({ id: 'apiKeys.table.created' }),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: formatDate,
        },
        {
            title: intl.formatMessage({ id: 'apiKeys.table.actions' }),
            key: 'actions',
            render: (record: IApiKey) => (
                <Space size="middle">
                    <Button
                        size="small"
                        onClick={() => onEditClicked(record)}
                        disabled={record.status !== 'active'}
                    >
                        {intl.formatMessage({ id: 'apiKeys.actions.edit' })}
                    </Button>
                    <Button
                        size="small"
                        danger
                        onClick={() => onRevokeClicked(record)}
                        disabled={record.status !== 'active'}
                    >
                        {intl.formatMessage({ id: 'apiKeys.actions.revoke' })}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="project-tab">
            <div style={{ marginBottom: '24px' }}>
                <Title level={4} style={{ margin: 0 }}>
                    {intl.formatMessage({ id: 'apiKeys.title' })}
                </Title>
                <Text type="secondary">
                    {intl.formatMessage({ id: 'apiKeys.description' })}
                </Text>
            </div>

            <Button
                type="primary"
                style={{ marginBottom: '16px' }}
                onClick={onCreateClicked}
            >
                {intl.formatMessage({ id: 'apiKeys.createButton' })}
            </Button>

            {apiKeys.length === 0 ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '48px 24px',
                    color: '#8c8c8c'
                }}>
                    <Text type="secondary">
                        {intl.formatMessage({ id: 'apiKeys.noKeys' })}
                    </Text>
                </div>
            ) : (
                <ResponsiveTable
                    dataSource={apiKeys}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                    mobileColumns={[
                        {
                            title: intl.formatMessage({ id: 'apiKeys.table.name' }),
                            key: 'apiKey',
                            render: (record: IApiKey) => (
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <div>
                                        <strong>{record.name}</strong>
                                        {formatStatus(record.status)}
                                    </div>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {record.keyPrefix}
                                    </Text>
                                    <Space size="small" wrap>
                                        {record.scopes.map((scope) => (
                                            <Tag key={scope} color="blue">
                                                {formatScope(scope)}
                                            </Tag>
                                        ))}
                                    </Space>
                                    {record.lastUsedAt && (
                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                            Last used: {formatDate(record.lastUsedAt)}
                                        </Text>
                                    )}
                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                        Created: {formatDate(record.createdAt)}
                                    </Text>
                                </Space>
                            ),
                        },
                        {
                            title: intl.formatMessage({ id: 'apiKeys.table.actions' }),
                            key: 'actions',
                            width: 120,
                            render: (record: IApiKey) => (
                                <Space direction="vertical" size="small">
                                    <Button
                                        size="small"
                                        onClick={() => onEditClicked(record)}
                                        disabled={record.status !== 'active'}
                                        style={{ width: '100%' }}
                                    >
                                        {intl.formatMessage({ id: 'apiKeys.actions.edit' })}
                                    </Button>
                                    <Button
                                        size="small"
                                        danger
                                        onClick={() => onRevokeClicked(record)}
                                        disabled={record.status !== 'active'}
                                        style={{ width: '100%' }}
                                    >
                                        {intl.formatMessage({ id: 'apiKeys.actions.revoke' })}
                                    </Button>
                                </Space>
                            ),
                        },
                    ]}
                />
            )}

            <Modal
                open={createModalOpen}
                onCancel={onCreateClose}
                centered
                footer={null}
                width={600}
                destroyOnClose
            >
                <ApiKeyCreate onClose={onCreateClose} />
            </Modal>

            <Modal
                open={editModalOpen}
                onCancel={onEditClose}
                centered
                footer={null}
                width={600}
            >
                {selectedApiKey && (
                    <ApiKeyEdit apiKey={selectedApiKey} onClose={onEditClose} />
                )}
            </Modal>
        </div>
    );
};

export default ApiKeys;